// FE-4 · Pages — every page the GEO site is made of, in the two shapes a client thinks in.
//
// Nothing new on the server: this file talks to R1's clusters (the buyer questions and the answer
// page each one has), R2's comparison pages and competitor list, R4's freshness clock and R5's
// list of pages AI actually cited. The page itself holds no rules — the state of a row, the three
// numbers in the summary line, the state of a matrix cell and the sentence shown for a refusal all
// live here, where a test can read them without a browser.
//
// Two decisions worth naming:
//
// * **"Live" and "needs a refresh" are different facts from different places.** A page's state is
//   R1's (`draft` / `published`); whether it has gone stale is R4's clock. A published page whose
//   freshness window has passed is shown as `stale`, never as a second kind of published.
// * **A citation is matched by URL, not by page id.** R5 reports what an engine quoted, which is
//   an address; `normalizeUrl` is the only place that decides two addresses are the same page.

import { featureIsOff } from "../stores/geoExecFeatures.js";
import { api } from "./client.js";
import { newIdempotencyKey, primaryQuestion } from "./distribution.js";

/** FE-5 · where "everything on your GEO site" lives now that the Pages page is gone.
 *
 * The answer-page list (R1) belongs to **AI Prompts Research**, which exists on `dev` and not on
 * this branch — see `docs/fe5-prompt-research-embed.md`. Until that view is merged the nearest
 * true destination is the comparison matrix under Sites, and this is the ONE line to repoint when
 * it lands: `{ path: "/prompts-research" }`. */
export const PAGES_HOME = { path: "/sites", query: { section: "comparisons" } };

const R1 = "/api/geo-exec/r1";
const R2 = "/api/geo-exec/r2";
const R4 = "/api/geo-exec/r4";

// ── requests ────────────────────────────────────────────────────────────────────────────────────

export function listClusters(brandId, { signal } = {}) {
  return api(`${R1}/brands/${encodeURIComponent(brandId)}/clusters`, { signal });
}

/** Write (and publish) the answer page for one buyer question. 402 while the plan is not Autopilot. */
export function writeAnswerPage(clusterId, { idempotencyKey = "" } = {}) {
  return api(`${R1}/clusters/${encodeURIComponent(clusterId)}/page`, {
    method: "POST",
    json: {},
    headers: { "Idempotency-Key": idempotencyKey || newIdempotencyKey() },
  });
}

export function listCompetitors(brandId, { signal } = {}) {
  return api(`${R2}/brands/${encodeURIComponent(brandId)}/competitors`, { signal });
}

export function listComparisonPages(brandId, { signal } = {}) {
  return api(`${R2}/brands/${encodeURIComponent(brandId)}/comparison-pages`, { signal });
}

/** One cell of the matrix: write the `vs` / `alternatives` page for one competitor. */
export function writeComparisonPage({ brandId, pageType, competitorIds = [], clusterId = null }, { idempotencyKey = "" } = {}) {
  const body = { brand_id: brandId, page_type: pageType, competitor_ids: competitorIds };
  if (clusterId != null) body.cluster_id = clusterId;
  return api(`${R2}/comparison-pages`, {
    method: "POST",
    json: body,
    headers: { "Idempotency-Key": idempotencyKey || newIdempotencyKey() },
  });
}

/** R5's effect payload. Only one field of it is read here — `cited_pages` — but the route is the
 *  same one the Impact page uses.
 *
 *  Written out rather than imported from `api/geoExecR5.js` on purpose: that module (like most of
 *  `api/`) imports `./client` without a file extension, which Vite resolves and bare node does
 *  not, and the Pages store has to be loadable by `node --test` for its "which answer is allowed
 *  to write" behaviour to be tested at all. Same reason for `brandRunHistory` below. */
export function listCitedPages(brandId, { signal } = {}) {
  return api(`/api/geo-exec/r5/brands/${encodeURIComponent(brandId)}/geo-site-effect`, { signal });
}

/** The brand's runs. The matrix reads Share of Answer out of the newest snapshot's competitors. */
export function brandRunHistory(brandId, { signal } = {}) {
  return api(`/api/brands/${encodeURIComponent(brandId)}/history`, { signal });
}

export function listFreshness(brandId, { signal } = {}) {
  return api(`${R4}/brands/${encodeURIComponent(brandId)}/freshness`, { signal });
}

/** R4's refresh is "fold this new data point in and republish", so it takes the fact, not a nudge. */
export function refreshPage(pageId, fact) {
  return api(`${R4}/pages/${encodeURIComponent(pageId)}/refresh`, { method: "POST", json: { fact } });
}

// ── the state of one page, as the client reads it ───────────────────────────────────────────────

export const PAGE_STATES = ["none", "draft", "published", "stale"];

const STATE_META = {
  none: { label: "No page", tone: "none", hint: "Nothing answers this question yet." },
  draft: { label: "Draft", tone: "draft", hint: "Written, not published." },
  published: { label: "Live", tone: "live", hint: "Published on your GEO site." },
  stale: { label: "Needs a refresh", tone: "stale", hint: "Live, but it has not been updated in a while." },
  cited: { label: "Cited by AI", tone: "cited", hint: "An AI answer quoted this page." },
  unavailable: { label: "Not available yet", tone: "off", hint: "" },
};

export function stateMeta(state) {
  return STATE_META[String(state || "")] || { label: String(state || ""), tone: "none", hint: "" };
}

/** `none` / `draft` / `published` / `stale` for one cluster. `fresh` is R4's row for its page.
 *
 * A retired page belongs to nobody (its cluster was merged away), so its cluster reads as
 * unanswered rather than as owning a page that is not there. */
export function pageState(cluster, fresh) {
  const page = cluster?.page;
  const state = String(page?.state || "");
  if (!page || !state || state === "retired") return "none";
  if (state !== "published") return "draft";
  return fresh?.due ? "stale" : "published";
}

// ── citations: which engines quoted which URL ───────────────────────────────────────────────────

/** Host + path, lowercased, no trailing slash, no query and no fragment.
 *
 * The two sides of the match are written by different systems — our own publisher and an engine's
 * answer — so `https://acme.example/pages/crm/` and `http://ACME.example/pages/crm?utm=x` have to
 * land on the same key or a cited page would silently show as uncited. */
export function normalizeUrl(url) {
  const raw = String(url || "").trim();
  if (!raw) return "";
  const stripped = raw.replace(/^https?:\/\//i, "").split("#")[0].split("?")[0];
  return stripped.replace(/\/+$/, "").toLowerCase();
}

/** R5's `cited_pages` → `{normalized url: [engine, …]}`. */
export function enginesByUrl(citedPages) {
  const out = {};
  for (const row of citedPages || []) {
    const key = normalizeUrl(row?.url);
    if (!key) continue;
    const list = (out[key] ||= []);
    for (const engine of row?.engines || []) {
      if (engine && !list.includes(engine)) list.push(engine);
    }
  }
  return out;
}

const ENGINES = {
  chat_gpt: { label: "ChatGPT", short: "GPT" },
  google_ai_overview: { label: "Google AI Overview", short: "AIO" },
  google: { label: "Google AI", short: "G" },
  gemini: { label: "Gemini", short: "GEM" },
  claude: { label: "Claude", short: "CL" },
  perplexity: { label: "Perplexity", short: "PX" },
};

/** One engine → the monogram chip a row shows. An engine we have no entry for still gets a chip:
 *  the answer came from somewhere, and printing nothing would read as "nobody quoted it". */
export function engineBadge(engine) {
  const id = String(engine || "").trim();
  if (!id) return null;
  const known = ENGINES[id.toLowerCase()];
  if (known) return { id, label: known.label, short: known.short };
  const label = id.replace(/[_-]+/g, " ").replace(/\b\w/g, (character) => character.toUpperCase());
  return { id, label, short: label.replace(/[^A-Za-z0-9]/g, "").slice(0, 3).toUpperCase() || "AI" };
}

export function engineBadges(engines) {
  return (engines || []).map(engineBadge).filter(Boolean);
}

// ── tab 1 · buyer questions ─────────────────────────────────────────────────────────────────────

/** R4's freshness list → `{page_id: row}`. */
export function freshnessByPage(freshness) {
  const out = {};
  for (const row of freshness || []) {
    if (row && row.page_id != null) out[row.page_id] = row;
  }
  return out;
}

/** The other phrasings of the same buyer question, minus the one already printed as the headline. */
export function variantQuestions(cluster) {
  const primary = primaryQuestion(cluster);
  const seen = new Set();
  return (cluster?.questions || [])
    .map((entry) => String(entry?.question || "").trim())
    .filter((question) => {
      if (!question || question === primary || seen.has(question)) return false;
      seen.add(question);
      return true;
    });
}

/** One row per buyer question: the question, the state of its page, who cited it, when it last moved. */
export function clusterRows(clusters, { freshness = [], citedPages = [] } = {}) {
  const due = freshnessByPage(freshness);
  const engines = enginesByUrl(citedPages);
  return (clusters || [])
    .filter((cluster) => cluster && String(cluster.state || "") !== "empty")
    .map((cluster) => {
      const page = cluster.page || null;
      const fresh = page ? due[page.page_id] || null : null;
      const url = String(page?.url || "");
      const cited = engines[normalizeUrl(url)] || [];
      const state = pageState(cluster, fresh);
      return {
        key: `cluster:${cluster.cluster_id}`,
        clusterId: cluster.cluster_id,
        question: primaryQuestion(cluster) || String(cluster.label || ""),
        label: String(cluster.label || ""),
        variants: variantQuestions(cluster),
        questionCount: (cluster.questions || []).length,
        pageId: page?.page_id ?? null,
        url,
        state,
        engines: cited,
        // The client asked "when was this last refreshed" — R4 answers that; a page nobody has
        // refreshed answers with the day it was published, which is the same question's answer.
        lastTouched: fresh?.last_refreshed_at ?? fresh?.last_published_at ?? page?.published_at ?? null,
        daysSince: fresh?.days_since ?? null,
        canWrite: state === "none",
        canRefresh: state === "published" || state === "stale",
        canOpen: Boolean(url),
      };
    });
}

/** The three numbers in the line above the list. `live` counts stale pages: they are still live. */
export function pagesOverview(rows) {
  const list = rows || [];
  return {
    live: list.filter((row) => row.state === "published" || row.state === "stale").length,
    needRefresh: list.filter((row) => row.state === "stale").length,
    unanswered: list.filter((row) => row.state === "none").length,
    drafts: list.filter((row) => row.state === "draft").length,
    cited: list.filter((row) => row.engines.length > 0).length,
    total: list.length,
  };
}

// ── tab 2 · the comparison matrix ───────────────────────────────────────────────────────────────
//
// Rows are competitors, columns are the three kinds of comparison page. Two of the three are keyed
// by competitor on the server (`vs`, `alternatives`) and can be written from a cell. The third is
// not: R2 keys a `best_for` page by INTENT CLUSTER (`r2/service.py` — page_key(page_type, cluster))
// so "write this one for this competitor" is not a thing the API can be asked. The column is drawn
// anyway, greyed and labelled, because leaving it out would say the page type does not exist.

export const COMPARISON_COLUMNS = [
  { id: "vs", label: "Versus", available: true, hint: "You against them, on the points buyers ask about." },
  { id: "alternatives", label: "Alternatives", available: true, hint: "The page people land on when they search for a way off them." },
  {
    id: "best_for",
    label: "Best for",
    available: false,
    hint: "A best-for page answers one buyer question rather than naming one rival, so it is not a cell in this grid — ask Maya for one.",
  },
];

function comparisonCell(column, page, engines) {
  const url = String(page?.url || "");
  const cited = url ? engines[normalizeUrl(url)] || [] : [];
  let state = column.available ? "none" : "unavailable";
  if (page) state = String(page.state || "") === "published" ? (cited.length ? "cited" : "published") : "draft";
  return {
    column: column.id,
    state,
    pageId: page?.page_id ?? null,
    url,
    engines: cited,
    canWrite: Boolean(column.available) && state === "none",
    canOpen: Boolean(url),
  };
}

function nameKey(name) {
  return String(name || "").trim().toLowerCase().replace(/\s+/g, " ");
}

/** The diagnosis' competitor list (`snapshot.competitors`) → `{name: share of answer}`.
 *
 * The same competitor can appear twice in a snapshot under two spellings; the stronger reading is
 * kept, because the column answers "how often does AI put them in the answer" and the lower of two
 * readings of the same company understates it. */
export function shareByName(competitors) {
  const out = {};
  for (const row of competitors || []) {
    const key = nameKey(row?.name);
    const value = Number(row?.rec_pct);
    if (!key || !Number.isFinite(value)) continue;
    out[key] = Math.max(out[key] ?? 0, value);
  }
  return out;
}

export function shareFor(share, name) {
  const value = (share || {})[nameKey(name)];
  return Number.isFinite(value) ? value : null;
}

/** The matrix: one row per kept competitor, ordered by who AI recommends most right now.
 *
 * Competitors the client removed are gone from the list rather than drawn as empty rows — the
 * server will not write a page for them, so a clickable cell would be a dead end. */
export function comparisonMatrix(competitors, pages, { citedPages = [], share = {} } = {}) {
  const engines = enginesByUrl(citedPages);
  const byCompetitor = {};
  for (const page of pages || []) {
    for (const entry of page?.competitors || []) {
      if (entry?.competitor_id == null) continue;
      const slot = (byCompetitor[entry.competitor_id] ||= {});
      const type = String(page.page_type || "");
      const current = slot[type];
      // A published page outranks a draft for the same cell: the draft is the one nobody can read.
      if (!current || (String(page.state || "") === "published" && String(current.state || "") !== "published")) {
        slot[type] = page;
      }
    }
  }
  const rows = (competitors || [])
    .filter((competitor) => competitor && competitor.kept !== false)
    .map((competitor) => ({
      key: `competitor:${competitor.competitor_id}`,
      competitorId: competitor.competitor_id,
      name: String(competitor.name || ""),
      domain: String(competitor.domain || ""),
      source: String(competitor.source || ""),
      share: shareFor(share, competitor.name),
      cells: COMPARISON_COLUMNS.map((column) =>
        comparisonCell(column, (byCompetitor[competitor.competitor_id] || {})[column.id] || null, engines),
      ),
    }));
  rows.sort((left, right) => {
    const a = left.share == null ? -1 : left.share;
    const b = right.share == null ? -1 : right.share;
    if (a !== b) return b - a;
    return left.name.localeCompare(right.name);
  });
  return rows;
}

/** How many comparison pages are live, for the tab's own one-line summary. */
export function comparisonOverview(rows) {
  const cells = (rows || []).flatMap((row) => row.cells);
  return {
    live: cells.filter((cell) => cell.state === "published" || cell.state === "cited").length,
    cited: cells.filter((cell) => cell.state === "cited").length,
    drafts: cells.filter((cell) => cell.state === "draft").length,
    open: cells.filter((cell) => cell.canWrite).length,
    competitors: (rows || []).length,
  };
}


// ── FE-5 · the two pieces AI Prompts Research embeds ────────────────────────────────────────────
//
// `AnswerPageCell` and `LosingBadge` are rendered one per PROMPT, on a page that lives on `dev`.
// Everything they decide is here, so that page can be written against tested functions rather
// than against a component's internals. See `docs/fe5-prompt-research-embed.md`.

/** The row (from `clusterRows`) that answers one question id, or null.
 *
 * A prompt with no row is `unlinked`: R1 has not put that question in a cluster, so there is no
 * page to open and none that could be written from here. It is a fifth state, not an empty one —
 * "no page yet" invites a Write page button that would 404. */
export function rowForQuestion(rows, clusters, questionId) {
  // `0` is a legitimate id; only null / undefined / "" mean "no question was passed".
  const wanted = questionId == null ? "" : String(questionId);
  if (!wanted) return null;
  const clusterIds = new Set();
  for (const cluster of clusters || []) {
    for (const entry of cluster?.questions || []) {
      if (String(entry?.question_id || "") === wanted) clusterIds.add(cluster.cluster_id);
    }
  }
  return (rows || []).find((row) => clusterIds.has(row.clusterId)) || null;
}

/** What one prompt's cell says, from R1's rows AND from whether R1 answered at all.
 *
 * `side` is `pagesState.answers`. It comes first on purpose: `unlinked` is a claim about the
 * PROMPT ("Maya has not grouped this one"), and a screen that has no rows yet — still loading, the
 * feature switched off, the read failed — has no basis for that claim about any prompt. Without
 * this, a research page with R1 off printed "Not grouped yet" on every row it had.
 *
 * Returns `loading` / `off` / `error` / `unlinked` / `none` / `draft` / `published` / `stale` /
 * `cited`. `side` is optional so a caller that already has rows can ask about a row alone. */
export function answerCellState(row, side = null) {
  if (side) {
    // A verdict about the deployment outranks everything: the rows are not coming.
    if (side.on === false) return "off";
    if (side.error) return "error";
    // A reload with rows already on screen keeps showing them rather than blinking to "Loading…".
    if (side.loading && !row) return "loading";
  }
  if (!row) return "unlinked";
  if (row.engines?.length && (row.state === "published" || row.state === "stale")) return "cited";
  return row.state;
}

const UNLINKED_META = { label: "Not grouped yet", tone: "off", hint: "Maya has not put this prompt in a buyer-question group, so there is no page to write from here." };

// The three states that are about the screen rather than about the prompt. Their words are in the
// catalogues (the cell is rendered inside a trilingual app), so `i18nKey` is what the component
// prints and `label` is only the fallback; `error` has no key because the sentence to print is the
// server's own, which the caller is holding.
const SIDE_META = {
  loading: { label: "Loading…", i18nKey: "pages_loading", tone: "off", hint: "" },
  off: { label: "Answer pages are not switched on for this workspace.", i18nKey: "pages_off_q", tone: "off", hint: "" },
  error: { label: "Could not read this prompt's page.", i18nKey: "", tone: "bad", hint: "" },
};

export function answerCellMeta(state) {
  if (state === "unlinked") return { ...UNLINKED_META, i18nKey: "" };
  return SIDE_META[state] || { ...stateMeta(state), i18nKey: "" };
}

/** One question's evidence as R1 stores it, falling back to the cluster's primary question. */
export function questionEvidence(cluster, questionId) {
  const questions = cluster?.questions || [];
  const wanted = questionId == null ? "" : String(questionId);
  const hit = questions.find((entry) => String(entry?.question_id || "") === wanted) || questions.find((entry) => entry?.role === "primary") || questions[0];
  return hit?.evidence || {};
}

/** Is this prompt one the brand is losing, and to whom?
 *
 * `engines` is deliberately NOT read as "these engines recommended them". R1 stores the run's
 * engine list on the question (`losing.py` — "which engines named a rival on THIS question is not
 * recoverable from the stored report"), so the badge reports who was named and how often, and
 * names the engines only as the ones that were asked. */
export function losingSummary(evidence) {
  const source = evidence || {};
  const rivals = (source.rivals || []).map((name) => String(name || "").trim()).filter(Boolean);
  const mentions = Number(source.rival_mentions) || 0;
  const samples = Number(source.samples) || 0;
  const engines = (source.engines || []).map((name) => String(name || "").trim()).filter(Boolean);
  return {
    losing: Boolean(String(source.basis || "") && rivals.length),
    basis: String(source.basis || ""),
    rivals,
    mentions,
    samples,
    engines,
    citedDomains: (source.cited_pages || []).map((domain) => String(domain || "").trim()).filter(Boolean),
  };
}

/** The badge's own sentence. Never "N engines recommended them" — see `losingSummary`. */
export function losingLine(summary) {
  if (!summary?.losing) return "";
  const who = summary.rivals.slice(0, 3).join(", ");
  const rest = summary.rivals.length > 3 ? ` and ${summary.rivals.length - 3} more` : "";
  if (summary.basis === "consecutive_runs" && !summary.mentions) return `AI named ${who}${rest} instead of you, twice in a row.`;
  if (!summary.samples) return `AI named ${who}${rest} instead of you.`;
  return `AI named ${who}${rest} in ${summary.mentions} of ${summary.samples} answers, and never you.`;
}

// ── refusals ────────────────────────────────────────────────────────────────────────────────────

const ERROR_FALLBACKS = {
  upgrade: "Writing pages is an Autopilot-plan feature.",
  page_cap_reached: "You are at your answer-page limit. Refresh one of the pages you have instead of adding another.",
  cluster_already_answered: "That question already has a page.",
  cluster_emptied_while_publishing: "Those questions moved to another group while the page was being written. Try again.",
  empty_cluster: "There are no questions left in this group, so there is nothing to answer.",
  no_verifiable_facts: "I could not verify enough about them to write an honest comparison, so I have written nothing.",
  best_for_cap_reached: "You are at your best-for page limit. Tell me which of the existing ones to rewrite.",
  no_domain: "I do not have their website address, and a competitor without one cannot be compared.",
  content_unchanged: "That data point did not change anything on the page, so the page has not been republished.",
  fact_not_used: "The rewrite did not use that data point, so the page is unchanged.",
  publish_failed: "The page did not go out. Your live page is untouched — try again in a moment.",
  page_not_found: "That page is gone.",
  not_found: "That is not there any more.",
};

/** One English sentence for a failed call, preferring the server's own wording.
 *
 * The server writes the sentence whenever it has numbers this code does not (the caps, the
 * paywall, the fact checks); we only supply words when it answered with a bare code. */
export function pagesErrorMessage(error) {
  const payload = error?.payload || {};
  const server = String(payload.msg || payload.message || "").trim();
  if (server) return server;
  const code = String(payload.error || "").trim();
  if (ERROR_FALLBACKS[code]) return ERROR_FALLBACKS[code];
  if (error?.status === 402) return ERROR_FALLBACKS.upgrade;
  return String(error?.message || "Something went wrong. Try again.");
}

/** Was this the feature switch saying "I am not here", rather than something the client did?
 *
 * A thin wrapper: `stores/geoExecFeatures.js` owns that rule for the whole app, and a second
 * copy of it here is exactly the kind of duplicate that drifts when the server's 404 body moves. */
export function isSwitchOff(error) {
  return featureIsOff(error);
}

export function formatStamp(seconds, locale) {
  if (!seconds) return "";
  const date = new Date(Number(seconds) * 1000);
  return Number.isNaN(date.getTime())
    ? ""
    : date.toLocaleDateString(locale || undefined, { year: "numeric", month: "short", day: "numeric" });
}
